import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Facebook, Instagram, Youtube, Loader2, RefreshCw, Check, Send, Edit3, X, AlertCircle, ExternalLink, Trash2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  getSocialAccounts,
  updateSocialAccount,
  getSocialPublications,
  getSocialPublication,
  updateSocialPublication,
  regenerateSocialPublication,
  approveSocialPublication,
  publishSocialPublication,
  deleteSocialPublication,
} from "../lib/api";

const PLATFORM_META = {
  facebook: { label: "Facebook", Icon: Facebook, color: "text-blue-600 bg-blue-50" },
  instagram: { label: "Instagram", Icon: Instagram, color: "text-pink-600 bg-pink-50" },
  youtube: { label: "YouTube", Icon: Youtube, color: "text-red-600 bg-red-50" },
};

const STATUS_LABELS = {
  draft: { label: "À valider", color: "bg-gray-200 text-gray-800" },
  ready: { label: "Prêt", color: "bg-blue-100 text-blue-800" },
  approved: { label: "Validé", color: "bg-emerald-100 text-emerald-800" },
  scheduled: { label: "Programmé", color: "bg-violet-100 text-violet-800" },
  publishing: { label: "En cours", color: "bg-amber-100 text-amber-800" },
  published: { label: "Publié", color: "bg-emerald-600 text-white" },
  failed: { label: "Erreur", color: "bg-red-100 text-red-800" },
  needs_media: { label: "Vidéo requise", color: "bg-orange-100 text-orange-800" },
};

const SOURCE_LABEL = {
  article: "Article",
  trip: "Carnet",
  trip_day: "Journée",
  deal: "Bon plan",
  review: "Test",
};

export default function DiffusionPage() {
  const [accounts, setAccounts] = useState([]);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("publications"); // publications | accounts
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, pubRes] = await Promise.all([getSocialAccounts(), getSocialPublications({})]);
      setAccounts(accRes.data || []);
      setPublications(pubRes.data || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = publications.filter((p) => {
    if (platformFilter !== "all" && p.platform !== platformFilter) return false;
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    return true;
  });

  const grouped = filtered.reduce((acc, p) => {
    const k = `${p.source_type}:${p.source_id}`;
    (acc[k] = acc[k] || { source_type: p.source_type, source_id: p.source_id, source_title: p.source_title, items: [] });
    acc[k].items.push(p);
    return acc;
  }, {});

  return (
    <div data-testid="diffusion-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">Diffusion</h1>
          <p className="text-muted-foreground text-sm">
            Centre de diffusion ITERCATUS — préparez et validez chaque publication avant envoi.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="gap-2" data-testid="reload-diffusion">
          <RefreshCw className="h-4 w-4" /> Rafraîchir
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={tab === "publications" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("publications")}
          data-testid="tab-publications"
        >
          Publications ({publications.length})
        </Button>
        <Button
          variant={tab === "accounts" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("accounts")}
          data-testid="tab-accounts"
        >
          Comptes sociaux
        </Button>
      </div>

      {tab === "accounts" ? (
        <AccountsPanel accounts={accounts} onChange={load} />
      ) : loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-[180px]" data-testid="platform-filter"><SelectValue placeholder="Plateforme" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes plateformes</SelectItem>
                {Object.entries(PLATFORM_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter-diffusion"><SelectValue placeholder="Statut" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {Object.keys(grouped).length === 0 ? (
            <div className="text-center py-12 bg-white border rounded-lg text-muted-foreground">
              Aucune publication.
              <div className="text-xs mt-2">
                Publiez un contenu (Article / Carnet / Bon plan…) pour générer automatiquement les brouillons sociaux
                sur les canaux activés.
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.values(grouped).map((grp) => (
                <div key={`${grp.source_type}:${grp.source_id}`} className="bg-white border rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase text-muted-foreground">{SOURCE_LABEL[grp.source_type] || grp.source_type}</div>
                      <div className="font-medium text-primary">{grp.source_title || "—"}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">{grp.items.length} publication(s)</div>
                  </div>
                  <div className="divide-y">
                    {grp.items.map((p) => (
                      <PublicationRow key={p.id} pub={p} onOpen={() => setDetailId(p.id)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {detailId && (
        <PublicationDetail
          pid={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function PublicationRow({ pub, onOpen }) {
  const meta = PLATFORM_META[pub.platform] || {};
  const Icon = meta.Icon || Facebook;
  const status = STATUS_LABELS[pub.status] || STATUS_LABELS.draft;
  return (
    <div className="px-4 py-3 flex items-center gap-3 hover:bg-muted/20" data-testid={`pub-row-${pub.id}`}>
      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${meta.color || "bg-gray-100"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-primary font-medium truncate">{pub.title || meta.label}</div>
        <div className="text-xs text-muted-foreground truncate">{(pub.caption || "").slice(0, 100)}</div>
      </div>
      <div className={`px-2 py-0.5 rounded text-xs font-medium ${status.color}`} data-testid={`pub-status-${pub.id}`}>
        {status.label}
      </div>
      <Button variant="outline" size="sm" onClick={onOpen} data-testid={`open-pub-${pub.id}`}>
        Ouvrir
      </Button>
    </div>
  );
}

function AccountsPanel({ accounts, onChange }) {
  const toggle = async (platform, patch) => {
    try {
      await updateSocialAccount(platform, patch);
      toast.success("Paramètres mis à jour");
      onChange();
    } catch (e) {
      toast.error("Erreur mise à jour");
    }
  };
  return (
    <div className="grid md:grid-cols-3 gap-4">
      {accounts.map((a) => {
        const meta = PLATFORM_META[a.platform] || {};
        const Icon = meta.Icon || Facebook;
        return (
          <div key={a.platform} className="bg-white border rounded-lg p-5" data-testid={`account-${a.platform}`}>
            <div className="flex items-center gap-2 mb-4">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${meta.color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-medium text-primary">{meta.label}</div>
                <div className="text-xs text-muted-foreground">
                  {a.connection_status === "connected" ? (a.display_name || "Compte connecté") : "Non connecté"}
                </div>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <label className="flex items-center justify-between gap-2">
                <span>Activé</span>
                <Switch
                  checked={!!a.enabled}
                  onCheckedChange={(v) => toggle(a.platform, { enabled: v })}
                  data-testid={`toggle-enabled-${a.platform}`}
                />
              </label>
              <label className="flex items-center justify-between gap-2">
                <span>Auto-brouillon à la publication</span>
                <Switch
                  checked={!!a.auto_draft_on_publish}
                  onCheckedChange={(v) => toggle(a.platform, { auto_draft_on_publish: v })}
                  data-testid={`toggle-auto-${a.platform}`}
                />
              </label>
              <div className="pt-3 border-t text-xs text-muted-foreground">
                {a.connection_status === "connected"
                  ? "Publication réelle disponible."
                  : "La publication réelle nécessite de connecter un compte (non disponible dans cette version)."}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PublicationDetail({ pid, onClose, onChanged }) {
  const [state, setState] = useState({ loading: true, pub: null, media: [] });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: "", caption: "", hashtags: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await getSocialPublication(pid);
      setState({ loading: false, pub: data.publication, media: data.media });
      setForm({
        title: data.publication.title || "",
        caption: data.publication.caption || "",
        hashtags: (data.publication.hashtags || []).join(" "),
      });
    } catch {
      toast.error("Erreur de chargement");
      onClose();
    }
  }, [pid, onClose]);

  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    setBusy(true);
    try {
      const hashtags = form.hashtags.split(/\s+/).filter((x) => x.startsWith("#"));
      await updateSocialPublication(pid, { title: form.title, caption: form.caption, hashtags });
      toast.success("Publication mise à jour");
      setEditing(false);
      await load();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Régénérer le contenu à partir de la source publiée ? Vos modifications manuelles seront écrasées.")) return;
    setBusy(true);
    try {
      await regenerateSocialPublication(pid);
      toast.success("Régénéré");
      await load();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await approveSocialPublication(pid);
      toast.success("Publication validée");
      await load();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!window.confirm("Publier immédiatement sur le réseau social ?")) return;
    setBusy(true);
    try {
      await publishSocialPublication(pid);
      toast.success("Publié");
      await load();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publication échouée");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer ce brouillon social ?")) return;
    try {
      await deleteSocialPublication(pid);
      toast.success("Supprimé");
      onChanged();
      onClose();
    } catch {
      toast.error("Erreur");
    }
  };

  if (state.loading) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-white" />
      </div>
    );
  }

  const p = state.pub;
  if (!p) return null;
  const meta = PLATFORM_META[p.platform] || {};
  const Icon = meta.Icon || Facebook;
  const status = STATUS_LABELS[p.status] || STATUS_LABELS.draft;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-auto" data-testid="publication-detail">
        <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${meta.color}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <div className="font-semibold text-primary">{meta.label}</div>
              <div className="text-xs text-muted-foreground">{p.source_title}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>{status.label}</span>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {p.last_error ? (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" /> {p.last_error}
            </div>
          ) : null}

          {p.status === "needs_media" && p.platform === "youtube" ? (
            <div className="bg-orange-50 border border-orange-200 text-orange-800 rounded p-3 text-sm">
              Attachez une vidéo au contenu source pour préparer cette publication YouTube.
            </div>
          ) : null}

          {editing ? (
            <div className="space-y-3">
              {p.platform === "youtube" ? (
                <div>
                  <label className="text-xs text-muted-foreground">Titre YouTube</label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-title" />
                </div>
              ) : null}
              <div>
                <label className="text-xs text-muted-foreground">
                  {p.platform === "youtube" ? "Description" : "Texte"}
                </label>
                <Textarea
                  rows={6}
                  value={form.caption}
                  onChange={(e) => setForm({ ...form, caption: e.target.value })}
                  data-testid="edit-caption"
                />
              </div>
              {p.platform !== "facebook" ? (
                <div>
                  <label className="text-xs text-muted-foreground">Hashtags (séparés par espaces, avec #)</label>
                  <Input value={form.hashtags} onChange={(e) => setForm({ ...form, hashtags: e.target.value })} data-testid="edit-hashtags" />
                </div>
              ) : null}
              <div className="flex gap-2">
                <Button onClick={saveEdit} disabled={busy} data-testid="save-edit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
                </Button>
                <Button variant="ghost" onClick={() => setEditing(false)}>Annuler</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {p.platform === "youtube" && p.title ? (
                <div className="font-medium text-primary">{p.title}</div>
              ) : null}
              <div className="whitespace-pre-line text-sm bg-muted/40 rounded p-3">{p.caption || "(vide)"}</div>
              {(p.hashtags || []).length > 0 ? (
                <div className="text-xs text-blue-700 flex flex-wrap gap-1">
                  {p.hashtags.map((t) => <span key={t} className="bg-blue-50 rounded px-1.5 py-0.5">{t}</span>)}
                </div>
              ) : null}
            </div>
          )}

          {/* Medias preview */}
          {state.media.length > 0 ? (
            <div>
              <div className="text-xs text-muted-foreground mb-2">Médias proposés ({state.media.length})</div>
              <div className="grid grid-cols-4 gap-2">
                {state.media.map((m) => (
                  <div key={m.id} className="aspect-square rounded overflow-hidden bg-muted">
                    {m.type === "video" ? (
                      <div className="w-full h-full flex items-center justify-center text-xs">Vidéo</div>
                    ) : (
                      <img src={m.public_cloudinary_url || m.original_cloudinary_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {p.external_url ? (
            <a href={p.external_url} target="_blank" rel="noreferrer" className="text-sm text-blue-700 inline-flex items-center gap-1">
              <ExternalLink className="h-3.5 w-3.5" /> Voir la publication
            </a>
          ) : null}
        </div>

        <div className="sticky bottom-0 bg-white border-t p-3 flex flex-wrap gap-2 justify-between">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing((x) => !x)} data-testid="edit-btn">
              <Edit3 className="h-4 w-4" /> {editing ? "Fermer l'édition" : "Modifier"}
            </Button>
            <Button variant="outline" size="sm" onClick={regenerate} disabled={busy} data-testid="regen-btn">
              <Sparkles className="h-4 w-4" /> Régénérer
            </Button>
            <Button variant="ghost" size="sm" onClick={remove} className="text-red-700" data-testid="delete-pub-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-2">
            {p.status !== "published" && p.status !== "approved" ? (
              <Button size="sm" onClick={approve} disabled={busy} data-testid="approve-btn">
                <Check className="h-4 w-4" /> Valider
              </Button>
            ) : null}
            {(p.status === "approved" || p.status === "ready" || p.status === "scheduled") ? (
              <Button size="sm" onClick={publish} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="publish-now-btn">
                <Send className="h-4 w-4" /> Publier maintenant
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
